import {
  MbbRouter,
  MbbSubscriptionOffering,
  MbbAllowedChangeInfo,
  MbbChangeInfo,
  Bundle,
} from './mbb-offerings-response.model';
import { Product } from '@shared/models/product.model';
import { Subscription } from '@shared/models/subscription-model';
import {
  BindingType,
  CustomerClassification,
  NumberCategory,
  ProductClassification,
} from '@shared/models/subscription-offering.model';
import { PaymentType } from '@shared/enums/payment-type';
import { OfferingVariants } from '@shared/enums/offering-variants.enum';
import { MbbDeliveryOptions } from './mbb-delivery-options.model';
import { BenefitProgram } from '@shared/enums/benefit-program-type.enum';
import { AssignedSubscriptionModel } from '@app/shared/models/assigned-subscription-offering.model';

export class MbbProduct extends Product {
  routerName: string;
  imei: string;
  icc: string;
  orderSim: boolean;
  deliveryOption: MbbDeliveryOptions;
  mbbRouter: MbbRouter;
  selectedBundle: Bundle;
  existingSubscription: Subscription;
  assignedSubscription: AssignedSubscriptionModel;
  allowedSubscriptionOfferings: MbbAllowedChangeInfo[];
  isLoadingSubscriptionOfferings: boolean;
  mbbSubscription: MbbSubscriptionOffering;
  selectedChangeInfo: MbbChangeInfo;
  paymentType: PaymentType;
  activationDate: Date | null;

  constructor() {
    super();
    this.mbbRouter = new MbbRouter();
    this.mbbSubscription = new MbbSubscriptionOffering();
    this.paymentType = PaymentType.Billing;
  }
}

export class MbbProductTestBuilder {
  private mbbProduct: MbbProduct = new MbbProduct();

  public Default() {
    this.mbbProduct.id = '1000';
    this.mbbProduct.deliveryOption = MbbDeliveryOptions.SellInStore;
    this.mbbProduct.imei = '123';
    this.mbbProduct.icc = '123';
    this.mbbProduct.routerName = '123';
    this.mbbProduct.mbbSubscription.name = 'subscription';
    this.mbbProduct.mbbRouter.name = 'router';
    return this;
  }

  public With15GbSubscription() {
    this.mbbProduct.mbbSubscription = {
      dataInfo: {
        dataOnNet: 15360,
        hasRollover: true,
      },
      id: '20000253',
      name: 'Mobilt Bredbånd 15 GB 50% i 3 måneder',
      shortName: 'Mobilt Bredbånd 15 GB',
      isActive: true,
      numberCategory: NumberCategory.MBB12,
      customerClassification: CustomerClassification.B2C,
      productClassification: ProductClassification.MBB,
      monthlyPrice: {
        price: {
          includingVAT: 149.5,
          excludingVAT: 119.6,
          vatPercentage: 25,
        },
        isCampaign: true,
        campaignPeriod: 3,
        originalPrice: {
          includingVAT: 299,
          excludingVAT: 239.2,
          vatPercentage: 25,
        },
        isCustomerDiscount: false,
      },
      dataVolume: 15360,
      dataVolumeText: '15GB',
      benefitProgram: BenefitProgram.None,
      commitmentPeriod: 12,
      variant: OfferingVariants.None,
      bindings: [
        {
          bindingType: BindingType.Handset,
          period: 12,
          expirationDate: null,
          terminationFee: {
            includingVAT: 900,
            excludingVAT: 720,
            vatPercentage: 25,
          },
          terminationAdminFee: null,
          terminationDiscountFee: null,
        },
      ],
      isEquipmentUpgradeCompatible: false,
    };

    return this;
  }

  public WithA1SmartrouterRouter() {
    this.mbbProduct.mbbRouter = {
      offeringId: '71ea87b65e61c707df32c8f1bb452c1a',
      name: 'A1 Smartrouter',
      gtin: '6921419200079',
      image: {
        id: '7b03b2fa-370a-4047-be3b-4d2403af87a5',
        downloadUri:
          'https://media.test.bluestonepim.com/344d5cd8-4703-4c92-9f74-437d80a8bfef/7b03b2fa-370a-4047-be3b-4d2403af87a5/JMp6wtP4PpZiVD28UBEG3X51P/1sQQ0P6nSupbA3QV9TmeVBbi7.jpg',
        previewUri:
          'https://media.test.bluestonepim.com/344d5cd8-4703-4c92-9f74-437d80a8bfef/7b03b2fa-370a-4047-be3b-4d2403af87a5/JMp6wtP4PpZiVD28UBEG3X51P/1sQQ0P6nSupbA3QV9TmeVBbi7.jpg?f=jpg&w=400',
        name: 'Smartrouter large',
        description: '',
        fileName: 'smartrouter-large.jpg',
        contentType: 'image/jpeg',
        labels: [],
      },
      bundles: [
        {
          offeringId: '28d9d1b99f3822a8f72ff7687e2d31ae',
          subscriptionOfferingId: '20000253',
          name: 'Mobilt Bredbånd 15 GB',
          subsidyId: '123',
          monthlySubscriptionPriceInfo: {
            includingVAT: 149.5,
            excludingVAT: 119.6,
            vatPercentage: 25,
          },
          discountLengthMonths: 3,
          hardwarePrice: {
            includingVAT: 299,
            excludingVAT: 239.2,
            vatPercentage: 25,
          },
        },
      ],
      productAvailability: 'available',
      isBackorderEnabled: false,
      externalRetailOnly: false,
      stock: 100,
    };
    this.mbbProduct.selectedBundle = {
      offeringId: 'f83a2ca842b8b956b30c5234e330381d',
      subscriptionOfferingId: '20000253',
      subsidyId: '213',
      name: 'Mobilt Bredbånd 15 GB',
      monthlySubscriptionPriceInfo: {
        includingVAT: 149.5,
        excludingVAT: 119.6,
        vatPercentage: 25,
      },
      discountLengthMonths: 3,
      hardwarePrice: {
        includingVAT: 199,
        excludingVAT: 159.2,
        vatPercentage: 25,
      },
    };
    return this;
  }

  public WithSendRouterInPost() {
    this.mbbProduct.deliveryOption = MbbDeliveryOptions.SendRouterInPost;
    return this;
  }

  public WithPaymentTypeUpFront() {
    this.mbbProduct.paymentType = PaymentType.Upfront;
    return this;
  }

  public WithPaymentTypePayWithNextInvoice() {
    this.mbbProduct.paymentType = PaymentType.PayWithNextInvoice;
    return this;
  }

  public WithExistingProduct() {
    this.mbbProduct.isExistingSubscription = true;
    return this;
  }

  public Build() {
    return this.mbbProduct;
  }
}
